import {json,postBiz,rows} from '../core/coreapi';

import {
  ActivityCandidate,
  ActivityPlanningWorkbenchModel,
  ActivityPlanningIndicatorScope,
  ActivityPlanningTreeScope,
  ActivityPlanningScopeCode,
  ActivityTemplate,
  BudgetLineRow,
  IndicatorCandidate,
  KpiPlanRow,
  TaskRow,
  PlanningTaskSaveRow,
} from './activityplanning.models';

import {
  normalizeActivityPlanningWorkbench,
  normalizeBudgetLine,
  normalizeIndicatorCandidate,
  normalizeTask,
  normalizeTaskSave,
} from './activityplanning.normalizers';

export const fetchActivityPlanningAxes=async(
  planningcycleid:number,
  search='',
):Promise<any[]> =>
  rows(
    await postBiz({
      s:'activityplanning',
      a:'axiscombo',
      pci:planningcycleid,
      sch:search||null,
    }),
  );

export const fetchActivityPlanningGlobalObjectives=async(
  planningcycleid:number,
  axiscode:string,
  search='',
):Promise<any[]> =>
  rows(
    await postBiz({
      s:'activityplanning',
      a:'globalobjectivecombo',
      pci:planningcycleid,
      axc:axiscode||null,
      sch:search||null,
    }),
  );

export const fetchActivityPlanningSpecificObjectives=async(
  planningcycleid:number,
  axiscode:string,
  globalobjectivecode:string,
  search='',
):Promise<any[]> =>
  rows(
    await postBiz({
      s:'activityplanning',
      a:'specificobjectivecombo',
      pci:planningcycleid,
      axc:axiscode||null,
      ogc:globalobjectivecode||null,
      sch:search||null,
    }),
  );

export const fetchActivityPlanningWorkbench=async(
  planningcycleid:number,
):Promise<ActivityPlanningWorkbenchModel>=>{
  const response=await postBiz({
    s:'activityplanning',
    a:'workbench',
    pci:planningcycleid,
  });

  return normalizeActivityPlanningWorkbench(json(response));
};

export const setPlanningCostCenter=async(
  planningcycleid:number,
  costcenterid:number,
  stamp:string,
)=>json(
  await postBiz({
    s:'bieprofile',
    a:'costcenterset',
    pci:planningcycleid,
    cci:costcenterid,
    stp:stamp,
  }),
);

export const declareActivityReady = async (
  activityplanid: number,
) => json(
  await postBiz({
    s: 'activityplanning',
    a: 'ready',
    api: activityplanid,
  }),
);

export const completeActivityPlanning = async (
  planningcycleid: number,
  costcenterid: number,
) => json(
  await postBiz({
    s: 'activityplanning',
    a: 'complete',
    pci: planningcycleid,
    cci: costcenterid,
  }),
);

export const fetchIndicatorScope=async(
  planningcycleid:number,
  axiscode='',
  globalobjectivecode='',
  specificobjectivecode='',
  search='',
):Promise<ActivityPlanningIndicatorScope>=>{
  const response=await postBiz({
    s:'activityplanning',
    a:'indicatorcombo',
    pci:planningcycleid,
    axc:axiscode||null,
    ogc:globalobjectivecode||null,
    osc:specificobjectivecode||null,
    sch:search||null,
  });
  const source:any=json(response)?.sd ?? json(response) ?? {};
  const items=Array.isArray(source?.items) ? source.items : [];
  return {
    scope:String(source?.scope||'OVERVIEW') as ActivityPlanningScopeCode,
    canplan:Boolean(source?.canplan),
    count:Number(source?.count||0),
    items:items.map(normalizeIndicatorCandidate),
  };
};

export const fetchActivityPlanningTree=async(
  planningcycleid:number,
  axiscode='',
  globalobjectivecode='',
  specificobjectivecode='',
):Promise<ActivityPlanningTreeScope>=>{
  const response=await postBiz({
    s:'activityplanning',
    a:'tree',
    pci:planningcycleid,
    axc:axiscode||null,
    ogc:globalobjectivecode||null,
    osc:specificobjectivecode||null,
  });
  const source:any=json(response)?.sd ?? json(response) ?? {};
  return {
    scope:String(source?.scope||'OVERVIEW') as ActivityPlanningScopeCode,
    canplan:Boolean(source?.canplan),
    kpiplancount:Number(source?.kpiplancount||0),
    activityplancount:Number(source?.activityplancount||0),
    taskplancount:Number(source?.taskplancount||0),
    tree:Array.isArray(source?.tree) ? source.tree : [],
  };
};

export const addIndicator=async(payload:{
  kpiid:number;
  planningcycleid:number;
  baselinevalue:number;
  targetvalue:number;
  thresholdmin?:number|null;
  thresholdmax?:number|null;
  description?:string;
})=>json(
  await postBiz({
    s:'activityplanning',
    a:'indicatoradd',

    kpi:payload.kpiid,
    pci:payload.planningcycleid,

    bsv:payload.baselinevalue,
    tgt:payload.targetvalue,
    thn:payload.thresholdmin??null,
    thx:payload.thresholdmax??null,
    dsc:payload.description??'',
  }),
);

export const saveIndicators=async(
  records:KpiPlanRow[],
)=>json(
  await postBiz({
    s:'activityplanning',
    a:'indicatorsave',
    rcd:records.filter(
      record=>
        record.__dirty
        ||record._state==='dirty'
        ||record._state==='deleted',
    ),
  }),
);

export const fetchActivityCandidates=async(
  kpiplanid:number,
  planningcycleid:number,
  costcentercode:string,
):Promise<ActivityCandidate[]> =>
  rows(
    await postBiz({
      s:'activityplanning',
      a:'activitycombo',
      kpi:kpiplanid,
      pci:planningcycleid,
      ccc:costcentercode,
    }),
  );

export const addActivity=async(payload:{
  kpiplanid:number;
  activityid:number;
  planningcycleid:number;
  weight:number;
  drivervalue?:number;
  targetvalue?:number;
  description?:string;
})=>json(
  await postBiz({
    s:'activityplanning',
    a:'activityadd',
    pci: payload.planningcycleid,
    kpi:payload.kpiplanid,
    aci:payload.activityid,
    wgt:payload.weight,
    dva:payload.drivervalue??0,
    tva:payload.targetvalue??0,
    dsc:payload.description??'',
  }),
);

export const fetchTaskCandidates = async (
  activityplanid: number,
): Promise<TaskRow[]> => {

  const response = await postBiz({
    s: 'activityplanning',
    a: 'tasks',
    api: activityplanid,
    rid: activityplanid,
  });

  return rows(response).map(
    (row: any, index: number) =>
      normalizeTask(row, index)
  );
};

export const fetchTaskRepository = async (
  activityplanid: number,
): Promise<TaskRow[]> => {
  const response = await postBiz({
    s: 'activityplanning',
    a: 'tasks',
    api: activityplanid,
    rid: activityplanid,
  });

  return rows(response).map(
    (row: any, index: number) =>
      normalizeTask(row, index)
  );
};

export const fetchPlanningTasks = async (
  activityplanid: number
): Promise<TaskRow[]> => {
  const response = await postBiz({
    s: 'activityplanning',
    a: 'tasks',
    api: activityplanid,
    rid: activityplanid,
  });

  return rows(response).map(
    (row: any, index: number) =>
      normalizeTask(row, index)
  );
};

export const savePlanningTasks = async (
  activityplanid: number,
  records: PlanningTaskSaveRow[],
): Promise<any[]> => {

  const response = await postBiz({
    s: 'activityplanning',
    a: 'tasksave',
    api: activityplanid,
    rid: activityplanid,
    rcd: records.map(normalizeTaskSave),
  });

  return rows(response);
};

export const fetchBudgetLines = async (
  taskplanid: number,
): Promise<BudgetLineRow[]> => {

  const response = await postBiz({
    s: 'activityplanning',
    a: 'budgetlines',
    tpi: taskplanid,
    rid: taskplanid,
  });

  return rows(response).map(
    (row: any, index: number) =>
      normalizeBudgetLine(
        row,
        taskplanid,
        index,
      ),
  );
};

export const saveBudgetLines=async(
  taskplanid:number,
  records:any[],
)=>json(
  await postBiz({
    s:'activityplanning',
    a:'budgetsave',
    tpi:taskplanid,
    rid:taskplanid,
    rcd:records,
  }),
);

export const fetchTemplates=async():Promise<ActivityTemplate[]>=>
  rows(
    await postBiz({
      s:'activityplanning',
      a:'templates',
    }),
  );

export const applyTemplate=async(
  activityplanid:number,
  templatecode:string,
)=>json(
  await postBiz({
    s:'activityplanning',
    a:'templateapply',
    api:activityplanid,
    rid:activityplanid,
    tpc:templatecode,
  }),
);

export const validateActivityPlanning=async(
  planningcycleid:number,
)=>json(
  await postBiz({
    s:'activityplanning',
    a:'validate',
    pci:planningcycleid,
  }),
);
